"use client";

import { create } from "zustand";
import { persist } from "zustand/middleware";

import type { ModuleId, SearchEntity } from "@/components/header/search-modules";

export const NAV_SEARCH_TTL_MS = 60 * 60 * 1000; // 60 minutes
const MAX_RECENT = 20;
const MAX_CACHED_ITEMS_PER_MODULE = 500;

export type RecentSelection =
  | {
      kind: "module";
      moduleId: ModuleId;
      label: string;
      href: string;
      at: number;
    }
  | {
      kind: "entity";
      moduleId: ModuleId;
      entityId: string;
      label: string;
      href: string;
      at: number;
    };

type CacheEntry = {
  items: SearchEntity[];
  fetchedAt: number;
};

type NavSearchState = {
  entitiesCache: Partial<Record<ModuleId, CacheEntry>>;
  moduleFrequency: Partial<Record<ModuleId, number>>;
  entityFrequency: Record<string, number>;
  recentSelections: RecentSelection[];

  entityKey: (moduleId: ModuleId, entityId: string) => string;
  getCacheEntry: (moduleId: ModuleId) => CacheEntry | undefined;
  isCacheFresh: (moduleId: ModuleId, ttlMs?: number) => boolean;
  setCachedEntities: (moduleId: ModuleId, items: SearchEntity[]) => void;
  pruneExpired: (ttlMs?: number) => void;

  bumpModule: (moduleId: ModuleId) => void;
  bumpEntity: (moduleId: ModuleId, entityId: string) => void;
  pushRecent: (selection: RecentSelection) => void;
  clearAll: () => void;
};

export const useNavSearchStore = create<NavSearchState>()(
  persist(
    (set, get) => ({
      entitiesCache: {},
      moduleFrequency: {},
      entityFrequency: {},
      recentSelections: [],

      entityKey: (moduleId, entityId) => `${moduleId}:${entityId}`,
      getCacheEntry: (moduleId) => get().entitiesCache[moduleId],
      isCacheFresh: (moduleId, ttlMs = NAV_SEARCH_TTL_MS) => {
        const entry = get().entitiesCache[moduleId];
        if (!entry) return false;
        return Date.now() - entry.fetchedAt < ttlMs;
      },
      setCachedEntities: (moduleId, items) => {
        const trimmed = items
          .filter((i) => i?.id)
          .slice(0, MAX_CACHED_ITEMS_PER_MODULE);
        set((state) => ({
          entitiesCache: {
            ...state.entitiesCache,
            [moduleId]: { items: trimmed, fetchedAt: Date.now() },
          },
        }));
      },
      pruneExpired: (ttlMs = NAV_SEARCH_TTL_MS) => {
        const now = Date.now();
        set((state) => {
          const next: Partial<Record<ModuleId, CacheEntry>> = { ...state.entitiesCache };
          (Object.keys(next) as ModuleId[]).forEach((moduleId) => {
            const entry = next[moduleId];
            if (!entry) return;
            if (now - entry.fetchedAt >= ttlMs) {
              delete next[moduleId];
            }
          });
          return { entitiesCache: next };
        });
      },

      bumpModule: (moduleId) => {
        set((state) => ({
          moduleFrequency: {
            ...state.moduleFrequency,
            [moduleId]: (state.moduleFrequency[moduleId] ?? 0) + 1,
          },
        }));
      },
      bumpEntity: (moduleId, entityId) => {
        const key = get().entityKey(moduleId, entityId);
        set((state) => ({
          entityFrequency: {
            ...state.entityFrequency,
            [key]: (state.entityFrequency[key] ?? 0) + 1,
          },
        }));
      },
      pushRecent: (selection) => {
        set((state) => {
          const withoutDupes = state.recentSelections.filter((s) => {
            if (selection.kind === "module") {
              return !(s.kind === "module" && s.moduleId === selection.moduleId);
            }
            return !(
              s.kind === "entity" &&
              s.moduleId === selection.moduleId &&
              s.entityId === selection.entityId
            );
          });
          return {
            recentSelections: [selection, ...withoutDupes].slice(0, MAX_RECENT),
          };
        });
      },
      clearAll: () => {
        set({
          entitiesCache: {},
          moduleFrequency: {},
          entityFrequency: {},
          recentSelections: [],
        });
      },
    }),
    { name: "nav-search-storage" },
  ),
);

